import type { ComponentProps, ReactNode } from 'react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';

/*
 * A PÍLULA DE CAMPO das três modais (aéreo, hotel, rent a car): o gatilho de
 * 36px, `rounded-full`, que abre o controle de um campo de busca — lugar, data,
 * hora, quem. É a ÚNICA receita da pílula: o `PlaceField`, o `SearchField`, o
 * `TravelerTrigger`, o QUANDO do planner de voo e a hora do rent a car vestem
 * as classes daqui, para a fileira não brigar por 1px de borda ou um degrau de
 * peso entre campos vizinhos.
 *
 * A anatomia, no vocabulário do `Button` do DS:
 *  - `outline` desenha o campo pela borda (o campo solto na página, a fileira
 *    do aéreo); `ghost` é o campo DENTRO de uma cápsula que já tem borda (a
 *    barra compacta do hotel). A borda existe nas duas peles (transparente na
 *    `ghost`) para o campo não mudar de tamanho ao trocar.
 *  - O valor é 14px em peso 500 (`text-sm font-medium`, o próprio texto do
 *    `Button`; o `text-ui` do DS mede o mesmo): o valor respondido é o que a
 *    pessoa lê na fileira. O que AINDA NÃO foi respondido desce para
 *    `font-normal` em `--sand-500`, o mesmo tom do glifo (ícone, retrato,
 *    caret) — um degrau abaixo do rótulo da coluna; é peso + cor que separam
 *    pergunta de resposta numa fileira sem casca. Um código (o IATA da praça)
 *    não ganha peso extra: ele se separa do resto pela cor apagada do título.
 *  - O caret fecha a pílula à DIREITA (`ml-auto`), nunca `justify-between`: com
 *    ícone + valor + caret, o `between` centrava o valor numa pílula mais larga
 *    que ele (o QUANDO do aéreo na coluna do celular).
 *  - O anel de ABERTO (ink a 25%): o campo dono da camada aberta se anuncia
 *    sozinho, por `aria-expanded` (botões do DS) ou `data-popup-open` (o
 *    gatilho do Base UI). Não é o anel de foco de teclado: aquele responde a
 *    uma ação da pessoa, este diz de qual campo é a camada.
 *  - O MESMO anel, ligado por `data-wanted`, é o de PENDENTE: o sistema
 *    apontando o próximo campo a responder (o preenchimento guiado das páginas
 *    determinísticas — o `.is-wanted` do multitrecho do aéreo, na mesma cor).
 *    Um anel só, com dois gatilhos: quem pede a vez e quem tem a camada aberta.
 *
 * O `.genui2` é do consumidor: nenhum token daqui resolve fora da raiz de tema
 * do DS, e os cards do genui-kit não vivem sob ela.
 */

export type FieldTriggerVariant = 'outline' | 'ghost';

/* `iuk-field-pill` is not a style: it is the hook `FIELD_PILL_TOUCH_SCOPE_CLASS`
   reaches the pill through. */
const PILL_CLASS =
  'iuk-field-pill h-9 max-w-full min-w-0 justify-start gap-1.5 rounded-full px-3 text-left transition-colors';

/**
 * The 44px TOUCH variant of the pill (#5663), opt-in by an ANCESTOR: it applies
 * to every pill below it while the nearest query container is narrower than
 * `sm` (640) — a form that stacks into a column on the phone. Only sizes change
 * (44 tall, text 16, glyph 18, portrait 24); weights and colors stay those of
 * the 36px pill. It goes on an element INSIDE the container: a container
 * query never reads the element that declares the container.
 */
export const FIELD_PILL_TOUCH_SCOPE_CLASS = [
  '@max-[theme(--breakpoint-sm)]:[&_.iuk-field-pill]:h-11',
  '@max-[theme(--breakpoint-sm)]:[&_.iuk-field-pill]:gap-2',
  '@max-[theme(--breakpoint-sm)]:[&_.iuk-field-pill]:text-base',
  '@max-[theme(--breakpoint-sm)]:[&_.iuk-field-pill_input]:text-base',
  '@max-[theme(--breakpoint-sm)]:[&_.iuk-field-pill_svg]:size-4.5',
  '@max-[theme(--breakpoint-sm)]:[&_.iuk-field-pill_[data-slot=avatar]]:size-6',
  '@max-[theme(--breakpoint-sm)]:[&_.iuk-field-pill_[data-slot=avatar-fallback]]:text-xs',
].join(' ');

/**
 * The BOX variant of the pill (#6322), opt-in by an ANCESTOR like the touch one: the same
 * field with the shape of a form field instead of the pill — radius 10 (`radius-lg`), the
 * glyph 8 from the value and, OPEN, a 1.5px `foreground` line (the 1px border plus a 0.5px
 * ring, so nothing moves) in place of the open ring and the `muted` fill. The ride planner
 * inside the block shell wears it (Figma H4.3, `6866:45068`).
 */
export const FIELD_BOX_SCOPE_CLASS = [
  '[&_.iuk-field-pill]:rounded-lg',
  '[&_.iuk-field-pill]:gap-2',
  '[&_.iuk-field-pill[data-popup-open]]:border-foreground',
  '[&_.iuk-field-pill[data-popup-open]]:bg-background',
  '[&_.iuk-field-pill[data-popup-open]]:ring-[0.5px]',
  '[&_.iuk-field-pill[data-popup-open]]:ring-foreground',
].join(' ');

/** O estado de ABERTO, pelos dois gatilhos (ver o cabeçalho): o anel e o fundo
 *  `muted` que o `Button` do DS já dá ao `aria-expanded` — repetido para o
 *  `data-popup-open` para o gatilho do Base UI (o `div` do `PlaceField`, o
 *  `Select`) abrir com a mesma cara. `data-wanted` é o anel de PENDENTE: só o
 *  anel, sem o fundo — o campo não está aberto, está sendo apontado. */
export const FIELD_TRIGGER_OPEN_RING_CLASS =
  'aria-expanded:ring-2 aria-expanded:ring-foreground/25 data-popup-open:bg-muted data-popup-open:ring-2 data-popup-open:ring-foreground/25 data-wanted:ring-2 data-wanted:ring-foreground/25';

/** O atributo do anel de PENDENTE (ver `FIELD_TRIGGER_OPEN_RING_CLASS`), pronto
 *  pra espalhar no gatilho: `{...wantedAttr(wanted)}`. Ausente quando falso —
 *  o anel lê a PRESENÇA do atributo, e um `data-wanted="false"` acenderia. */
export function wantedAttr(wanted: boolean | undefined): { 'data-wanted'?: '' } {
  return wanted ? { 'data-wanted': '' } : {};
}

/** O glifo do campo (ícone à esquerda, caret à direita): 14px, tom de rótulo. */
export const FIELD_TRIGGER_GLYPH_CLASS = 'size-3.5 shrink-0 text-(--sand-500)';

/** O caret, quando a pílula é mais larga que o valor: gruda na ponta direita. */
export const FIELD_TRIGGER_CARET_CLASS = `${FIELD_TRIGGER_GLYPH_CLASS} ml-auto`;

/** O valor AINDA NÃO respondido ("A definir", "Para onde?"). */
export const FIELD_TRIGGER_EMPTY_CLASS = 'text-(--sand-500) font-normal';

/** O que o `Button` do DS já traz (flex, borda, cor por variante) e uma pílula
 *  que NÃO é botão precisa escrever por conta própria — o gatilho do
 *  `PlaceField` é um `div` (hospeda o input quando aberto) e o espelho do
 *  QUANDO congelado é um `span`. As cores repetem as do `outline`/`ghost` do
 *  `Button` de propósito: é o mesmo campo, e ele não pode mudar de cara por
 *  ter virado `div`. */
const STANDALONE_CLASS = 'flex items-center border text-sm font-medium';
const STANDALONE_VARIANT_CLASS: Record<FieldTriggerVariant, string> = {
  outline: 'border-border bg-background dark:border-input dark:bg-input/30',
  ghost: 'border-transparent',
};
/* O foco de TECLADO do `Button` do DS (`buttonVariants`), para o `div` focável
   não cair no outline do reset enquanto os botões vizinhos mostram o anel. */
const STANDALONE_FOCUS_CLASS =
  'outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50';

/**
 * As classes da pílula para um elemento que NÃO é o `Button` do DS. `interactive`
 * liga o hover e o anel de aberto; um espelho estático (o QUANDO do card
 * congelado) não abre nada e não os recebe.
 */
export function fieldPillClass({
  variant = 'outline',
  interactive = true,
  fill = false,
}: {
  readonly variant?: FieldTriggerVariant;
  readonly interactive?: boolean;
  readonly fill?: boolean;
} = {}): string {
  return [
    STANDALONE_CLASS,
    STANDALONE_VARIANT_CLASS[variant],
    PILL_CLASS,
    interactive
      ? `hover:bg-muted hover:text-foreground ${STANDALONE_FOCUS_CLASS} ${FIELD_TRIGGER_OPEN_RING_CLASS}`
      : '',
    fill ? 'w-full' : '',
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * A pílula como `Button` do DS. `fill` ocupa a coluna (a página dá 192/208 ao
 * campo e o botão do DS nasce `inline-flex shrink-0`, largura do conteúdo); no
 * card a fileira é `w-fit` e cada pílula mede o que o valor pede. `justify`
 * `between` manda o caret para a ponta direita quando a pílula é mais larga que
 * o valor (o QUANDO do aéreo, com piso de largura). Sem `className`: a pele é
 * daqui; ganchos de CSS do host entram por `data-*`, que o `Button` repassa.
 */
export function FieldTrigger({
  variant = 'outline',
  fill = false,
  children,
  ...props
}: Omit<ComponentProps<typeof Button>, 'variant' | 'size' | 'className'> & {
  readonly variant?: FieldTriggerVariant;
  readonly fill?: boolean;
  readonly children?: ReactNode;
}): ReactNode {
  return (
    <Button
      type="button"
      variant={variant}
      className={`${PILL_CLASS} ${FIELD_TRIGGER_OPEN_RING_CLASS}${fill ? ' w-full' : ''}`}
      {...props}
    >
      {children}
    </Button>
  );
}
